// DEALS + EOI DESKTOP TABLE CHROME — owner task 2026-10-29.
//
// Small layout edits on the two EXISTING desktop (>768px) tables, nothing else:
//   • Phone + Alt. Phone are ONE cell (phone line 1, alt phone muted line 2,
//     "—" when empty) — the Alt. Phone column is gone.
//   • Project + Developer (Deals) / Project + Unit Type (EOI) are ONE cell the
//     same way — the Developer / Unit Type columns are gone.
//   • the cancelled chip reads "✕ Cancelled"; "N approved" is gone and
//     "⏳ N pending approval" is an amber pill beside the chips, HIDDEN at 0.
//   • the Source cell colours the source NAME through ONE shared map.
//
// Everything here is DESKTOP ONLY. The mobile cards (p.isMobile) on both pages
// render exactly what they rendered before and import nothing from this file.
// Role visibility is untouched: each merged cell renders under the same role
// predicate the two columns it replaces rendered under.
import React from "react";
import { leadSourceColor } from "./leadSources";

// ONE colour per source name on the two pages, read from the lead source
// registry (src/lib/leadSources.js — the server's list, 2026-11-02): every
// canonical source plus "Daily Request", which a DR-born deal carries as its
// source. A value in neither keeps the cell's default colour and weight —
// never a guess.
//
// The style the source NAME takes: the registry colour at weight 600, or {} so an
// unknown value renders exactly as the cell always did.
export function sourceNameStyle(source) {
  var c = leadSourceColor(source);
  return c ? { color: c, fontWeight: 600 } : {};
}

// Desktop label of the "cancelled" chip on both pages. The tab registries
// (EOI_TABS / DEALS_TABS) keep their labels — the mobile strip still reads them.
export var TXN_CANCELLED_CHIP_LABEL = "✕ Cancelled";
// Since the mobile-parity pass (owner 2026-10-29) the phone strip reads
// "✕ Cancelled" too — "Deal Cancelled" is gone from both layouts. The second
// argument is kept so every caller stays byte-identical.
export function txnChipLabel(tab, isMobile) {
  return (tab && tab.id === "cancelled") ? TXN_CANCELLED_CHIP_LABEL : tab.label;
}

// EOI tab text (owner 2026-10-29): the count shows ONLY on the selected tab —
// "📋 Active (5)" · "✕ Cancelled" while Active is selected, "📋 Active" ·
// "✕ Cancelled (11)" while Cancelled is. Deals tabs stay label-only.
export function eoiTabText(tab, isSelected, count) {
  return txnChipLabel(tab, false) + (isSelected ? " (" + (Number(count) || 0) + ")" : "");
}

// The phone header's ⚖️ Project Weights button (owner 2026-10-29): a 36px
// outlined amber square beside "+ Add Deal", same modal, same gate as desktop.
export function ProjectWeightsIconBtn(p) {
  return <button type="button" data-project-weights="1" title="Project Weights" aria-label="Project Weights" onClick={p.onClick}
    style={{ width: 36, height: 36, minHeight: 36, maxHeight: 36, minWidth: 36, borderRadius: 8, border: "1px solid #D97706", background: "#FFFBEB", color: "#B45309", fontSize: 16, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, padding: 0 }}>🎯</button>;
}

// "⏳ N pending approval" — amber pill beside the Active / Cancelled chips.
// Renders NOTHING at 0: a pill that says "0 pending" is noise, not information.
export var PENDING_PILL_STYLE = {
  alignSelf: "center", display: "inline-flex", alignItems: "center", whiteSpace: "nowrap",
  padding: "5px 12px", borderRadius: 9, fontSize: 12, fontWeight: 600,
  background: "#FFFBEB", border: "1px solid #FCD34D", color: "#B45309", marginLeft: 4
};
// 2026-10-29 (owner task, third pass): the pill is a TOGGLE. `n` is the
// ALL-TIME pending count (never period-scoped); hidden at 0 unless it is the
// active filter (so the ✕ can still clear it). `onClick` focuses/clears the
// pending-only filter — the page owns what "focus" means (Deals also jumps the
// period to All and restores it on clear); `onClear` is the ✕.
export var PENDING_PILL_ACTIVE_STYLE = { background: "#FDE68A", border: "1px solid #D97706", color: "#78350F", cursor: "pointer" };
// `short` (the EOI phone strip) reads "⏳ N pending"; `compact` (the Deals
// phone strip, owner 2026-10-30) reads just "⏳ N" — the number only; `style`
// lets a row push the pill to its end (marginLeft:auto). Same count, same
// toggle, same ✕ whichever label it wears.
export function PendingApprovalPill(p) {
  var n = Number(p.n) || 0, active = !!p.active;
  if (n <= 0 && !active) return null;
  var style = Object.assign({}, PENDING_PILL_STYLE, p.onClick ? { cursor: "pointer" } : {}, active ? PENDING_PILL_ACTIVE_STYLE : {}, p.style || {});
  var word = p.compact ? "" : p.short ? " pending" : " pending approval";
  return <button type="button" data-pending-pill="1" data-pending-active={active ? "1" : "0"} data-pending-compact={p.compact ? "1" : "0"} title={p.title || "Awaiting approval"} style={style} onClick={p.onClick}>
    ⏳ {n}{word}{active && <span data-pending-clear="1" title="Clear this filter" onClick={function (e) { e.stopPropagation(); if (p.onClear) p.onClear(); }} style={{ marginLeft: 8, fontWeight: 700 }}>✕</span>}
  </button>;
}

// The desktop header buttons on both pages (owner 2026-10-30): "+ Add Deal" /
// "+ Add EOI" (orange, the Btn gradient) take the SAME padding, radius and
// font-size as the outlined "Project Weights" beside them, plus a transparent
// 1px border so the two box heights are equal to the pixel. One object, read
// by all three buttons.
export var TXN_HEADER_BTN_STYLE = { padding: "7px 13px", fontSize: 12, borderRadius: 10 };
export var TXN_ADD_BTN_STYLE = Object.assign({ border: "1px solid transparent" }, TXN_HEADER_BTN_STYLE);

// The Deals "Source" dropdown options (owner 2026-10-29): the registry's source
// names the forms offer plus "Daily Request" (the source a DR-born lead carries),
// every label coloured through the same registry colour the table uses. The
// picker reads `color` per option; an unknown source falls back to the default.
export function dealSourceOptions(sources) {
  var list = (sources || []).slice();
  if (list.indexOf("Daily Request") < 0) list.push("Daily Request");
  return list.map(function (s) { var st = sourceNameStyle(s); return { value: s, label: s, color: st.color || undefined }; });
}

// ONE tab-chip style for the Deals and EOI strips (owner 2026-10-29): the
// selected chip is a 1.5px border in the tab's own colour over its light
// background; the idle chip is a hairline. `inactiveColor` is the page's
// C.textLight (the lib has no C). On the phone every chip stretches (flex:1).
export function txnTabStyle(tab, active, isMobile, inactiveColor) {
  var base = { padding: "7px 14px", borderRadius: 9, border: active ? "1.5px solid " + tab.fg : "1px solid #E8ECF1", background: active ? tab.bg : "#fff",
    color: active ? tab.fg : inactiveColor, fontSize: 12, fontWeight: active ? 700 : 600, cursor: "pointer", boxSizing: "border-box" };
  return isMobile ? Object.assign(base, { flex: 1, textAlign: "center" }) : base;
}

// The header's Total pill follows the selected tab (owner 2026-10-29): Active
// keeps the page colour; Cancelled takes the Cancelled tab's tokens on both
// pages. Since 2026-10-30 (owner task) the EOI Active tokens ARE the Deals
// green — the EOI purple is gone from the pill and the tab alike.
export var TXN_TOTAL_ACTIVE = { deals: { color: "#16A34A", background: "#DCFCE7" }, eoi: { color: "#16A34A", background: "#DCFCE7" } };
export var TXN_TOTAL_CANCELLED = { color: "#B91C1C", background: "#FEE2E2" };
export function txnTotalPillStyle(kind, tab, activeColor) {
  var tone = tab === "cancelled" ? TXN_TOTAL_CANCELLED : Object.assign({}, TXN_TOTAL_ACTIVE[kind] || TXN_TOTAL_ACTIVE.deals, activeColor ? { color: activeColor } : {});
  return Object.assign({ fontSize: 13, fontWeight: 700, padding: "5px 14px", borderRadius: 20 }, tone);
}

// A small red dot on the Cancelled tab chip when ≥1 cancelled row exists in the
// page's current scope (Deals: the selected period; EOI: its whole set, it has
// no period). The chips carry no counts — they are tabs.
export function CancelledDot(p) {
  if (!p.show) return null;
  return <span data-cancelled-dot="1" title={p.title || "Cancelled rows in this period"} style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: "#DC2626", marginLeft: 6, verticalAlign: "middle" }}/>;
}

// The "Approved" status column on both tables (owner 2026-10-29): a fixed
// minimum width and a nowrap pill. Under table-layout:auto a long second line
// in the Project cell (an EOI's unit-type / notes string) grew that column and
// squeezed this one until "❌ Cancelled" wrapped onto two lines on narrower
// windows. 112px = the widest pill ("❌ Cancelled", 11px bold) + the cell padding.
export var APPROVED_COL_PIN = { width: 112, minWidth: 112, boxSizing: "border-box", whiteSpace: "nowrap" };
// A stacked cell's second line held to ONE line with an ellipsis (the full text
// rides on the title tooltip) — the other half of the same fix.
export var STACKED_ELLIPSIS_STYLE = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220 };

// Two lines in one cell: `top` keeps the cell's own style, `bottom` is muted.
// `bottom` renders "—" when the caller passes nothing (null / "" / undefined).
// `bottomTitle` is the tooltip for an ellipsised second line.
export var STACKED_MUTED_STYLE = { fontSize: 11, color: "#94A3B8", marginTop: 2 };
export function StackedCell(p) {
  var hasBottom = p.bottom !== null && p.bottom !== undefined && p.bottom !== "" && p.bottom !== false;
  return <div>
    <div>{p.top}</div>
    <div style={Object.assign({}, STACKED_MUTED_STYLE, p.bottomStyle || {})} title={p.bottomTitle || undefined}>{hasBottom ? p.bottom : "—"}</div>
  </div>;
}
